import { createFileRoute, notFound, rootRouteId } from '@tanstack/react-router';

import { buildGuards, hasPermission } from '@mint-lib/routing-guards';
import { MediaPlan } from '@mint-sdk/api-media-plan';
import {
  PlacementColumns,
  VirtualSettings,
} from '@mint-sdk/api-virtual-settings';
import { socket } from '@mint-sdk/api-virtual-settings/socket';
import { io } from 'socket.io-client';
import { z } from 'zod';

import { isAxiosError } from '../../../../../../../../utils/helpers.js';

const guard = buildGuards(hasPermission('campaignSettingsFeature:view'));
const ITEM_NOT_CONFIGURED = 'item_not_configured';

const port = globalThis.location.port;
const isSecure = port === '' || port === '443';
const protocol = isSecure ? 'wss' : 'ws';
const portSuffix = port === '443' ? '' : `:${port}`;
const WS_URL = `${protocol}://${globalThis.location.hostname}${portSuffix}`;

const establishSocketConnection = async (accessToken: string) => {
  const rawSocket = io(
    `${WS_URL}/api/planning-service/socket-io/virtual-configuration-updates/`,
    {
      transports: ['websocket'],
      path: '/api/planning-service/socket-io',
      query: {
        authorization: encodeURIComponent(accessToken),
      },
    },
  );

  socket.provideClient(rawSocket);
};

export const Route = createFileRoute(
  '/_secured/campaign/$campaignId/settings/ghost/$tileId/settings/',
)({
  async beforeLoad({ context }) {
    const guardsContext = await context.getGuardContext();

    if (!guard(guardsContext)) {
      throw notFound();
    }
  },

  validateSearch: z.object({
    mediaPlan: z.union([z.string(), z.number()]).optional(), //make it z.string().optional() with ff.ghostV2.enabled
    activity: z.string().optional(),
    flight: z.string().optional(),
  }),
  async loader({ context, params }) {
    const isGhostV2Enabled = await context.isFlagEnabled(
      '@mint/arm',
      'ghostV2',
    );

    const authService = await context.getInstance('auth');
    const accessToken = authService.getAccessToken();
    if (accessToken) {
      await establishSocketConnection(accessToken);
    }

    isGhostV2Enabled
      ? await Promise.all([
          context.getInstance('i18n', '@myn/ghost_v2'),
          context.getInstance('i18n', '@mint-lib/ag-grid'),
        ])
      : await context.getInstance('i18n', '@myn/ghost');

    const i18n = await context.getInstance('i18n', '@mint/routing');
    await context.getInstance('i18n', '@myn/campaign-settings');
    //TODO: add logic for MINT 2.0
    const title = i18n.t('Settings | MINT');

    if (!isGhostV2Enabled) {
      return { title, virtualDspCampaignId: '' };
    }

    const isGhostMdmEnabled = await context.isFlagEnabled(
      '@mint/arm',
      'ghostMdm',
    );

    try {
      const mediaItemPromise = context.prefetchQuery({
        ...MediaPlan.getMediaItemDetailsQueryOptions({
          urlParams: { mediaItemId: params.tileId },
        }),
        staleTime: 1000 * 30,
      });

      const virtualDspCampaignPromise = context.prefetchQuery({
        ...VirtualSettings.getVirtualDspCampaignQueryOptions({
          params: {
            mediaItemId: params.tileId,
          },
        }),
        staleTime: Infinity,
      });

      const [mediaItemQueryResult, virtualDspCampaignQueryResult] =
        await Promise.all([mediaItemPromise, virtualDspCampaignPromise]);

      const isItemConfigured =
        mediaItemQueryResult.data?.items[0]?.isConfigured;
      const virtualDspCampaignId =
        virtualDspCampaignQueryResult.data?.items[0]?.id;

      if (!isItemConfigured || !virtualDspCampaignId) {
        throw new Error(ITEM_NOT_CONFIGURED);
      }

      if (isGhostMdmEnabled) {
        context.prefetchQuery({
          ...PlacementColumns.getPlacementMdmColumnListQueryOptions({
            params: { mediaItemId: params.tileId },
          }),
          staleTime: Infinity,
        });
      }

      return {
        title,
        virtualDspCampaignId,
      };
    } catch (error) {
      const isNotConfiguredItemError =
        error instanceof Error && error.message === ITEM_NOT_CONFIGURED;
      const isNotFoundError = isAxiosError(error) && error.status === 404;

      if (isNotConfiguredItemError || isNotFoundError) {
        throw notFound({ routeId: rootRouteId });
      }

      throw error;
    }
  },
  head: ({ loaderData }) => ({
    meta: [
      {
        title: loaderData?.title,
      },
    ],
  }),
  onLeave: async () => {
    socket.getClient().disconnect?.();
  },
});
